import { useQuery, type QueryKey } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { buildProjectsTableColumns } from '@/features/projects/components/ProjectsTable.columns';
import { queryProjectSummaries } from '@/features/projects/data';
import { projectsGridConfig } from '@/features/projects/grid/gridConfig';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { queryKeys } from '@/shared/hooks';
import type { ProjectSummaryRow } from '@/features/projects/components/ProjectsTable.types';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColDef } from '@/ui/primitives';
import type { ProjectDto, ProjectSummaryDto, QueryResult } from '@flowqa/shared';

type UseProjectsTableControllerInput = {
  persistKey?: string | null;
  syncStateToUrl: boolean;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<ProjectDto>>;
  queryKey?: QueryKey;
  summariesQueryFn?: (projectIds: string[]) => Promise<ProjectSummaryDto[]>;
  summariesQueryKey?: QueryKey;
  onRowsChange?: (rows: ProjectDto[]) => void;
};

type ProjectsTableController = {
  columns: Array<GridColDef<ProjectSummaryRow>>;
  gridConfig: ReturnType<typeof projectsGridConfig>;
  handleRowsChange: (rows: ProjectDto[]) => void;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<ProjectDto>>;
  queryKey: QueryKey;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
};

export const useProjectsTableController = ({
  persistKey,
  syncStateToUrl,
  queryFn,
  queryKey,
  summariesQueryFn,
  summariesQueryKey,
  onRowsChange,
}: UseProjectsTableControllerInput): ProjectsTableController => {
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const [visibleProjectIds, setVisibleProjectIds] = useState<string[]>([]);
  const projectIds = useMemo(
    () => Array.from(new Set(visibleProjectIds)).sort(),
    [visibleProjectIds],
  );
  const projectIdsKey = useMemo(() => JSON.stringify(projectIds), [projectIds]);
  const canQueryRelated = projectIds.length > 0;
  const summariesQuery = useQuery({
    queryKey:
      summariesQueryKey ??
      queryKeys.projectSummaries(selectedOrganisationId ?? null, projectIdsKey),
    enabled: canQueryRelated,
    queryFn: async () =>
      summariesQueryFn
        ? await summariesQueryFn(projectIds)
        : await queryProjectSummaries(projectIds, selectedOrganisationId ?? undefined),
  });
  const summariesByProjectId = useMemo(() => {
    const map = new Map<string, ProjectSummaryDto>();
    const items = summariesQuery.data ?? [];
    items.forEach((summary) => {
      map.set(summary.projectId, summary);
    });
    return map;
  }, [summariesQuery.data]);
  const columns = useMemo(
    () =>
      buildProjectsTableColumns({
        summariesByProjectId,
        onSelectProject: setSelectedProjectId,
      }),
    [setSelectedProjectId, summariesByProjectId],
  );
  const handleRowsChange = useCallback(
    (rows: ProjectDto[]) => {
      setVisibleProjectIds(rows.map((row) => row.id));
      onRowsChange?.(rows);
    },
    [onRowsChange],
  );
  const gridConfig = projectsGridConfig({
    orgId: selectedOrganisationId,
    persistKey: persistKey ?? 'projects-table',
    syncStateToUrl,
  });

  return {
    columns,
    gridConfig,
    handleRowsChange,
    queryFn: queryFn ?? gridConfig.queryFn,
    queryKey: queryKey ?? gridConfig.queryKey,
    selectedOrganisationId,
    selectedProjectId,
  };
};
